import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, ArrowRight, Plus, Trash2, CheckCircle, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status, t }) => {
  const styles = {
    assigned: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    completed: 'bg-blue-100 text-blue-800 border border-blue-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
      {t(status)}
    </span>
  );
};

const StatCard = ({ label, value, color, language }) => (
  <Card className="p-4">
    <p className="text-xs text-muted-foreground mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{label}</p>
    <p className={`text-2xl font-bold ${color || ''}`}>{value}</p>
  </Card>
);

export const OutsourcingWorker = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { t, language } = useLanguage();

  const [worker, setWorker] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit worker dialog
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', notes: '' });

  // New job dialog
  const [isJobOpen, setIsJobOpen] = useState(false);
  const [jobForm, setJobForm] = useState({ order_id: '', order_number: '', item_code: '', description: '', work_type: 'full', quantity: '', cost_per_piece: '', notes: '' });
  const [selectedOrderTotal, setSelectedOrderTotal] = useState(null);
  const [selectedOrderAssigned, setSelectedOrderAssigned] = useState(0);

  // Payment dialog
  const [isPayOpen, setIsPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' });

  const loadData = async () => {
    try {
      const [wRes, jRes, pRes, oRes, rRes] = await Promise.all([
        axios.get(`${API}/outsourcing/workers/${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/jobs?worker_id=${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/payments?worker_id=${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/rates`, { headers: getAuthHeader() }),
      ]);
      setWorker(wRes.data);
      setJobs(jRes.data);
      setPayments(pRes.data);
      setOrders(oRes.data.filter(o => ['pending', 'in_production'].includes(o.status)));
      setRates(rRes.data);
    } catch {
      toast.error('Failed to load worker data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  const totalWork = jobs.filter(j => j.status === 'completed' && j.worker_id === id).reduce((s, j) => s + (j.total_cost || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = totalWork - totalPaid;

  // --- Edit worker ---
  const openEdit = () => {
    setEditForm({ name: worker.name, phone: worker.phone || '', notes: worker.notes || '' });
    setIsEditOpen(true);
  };

  const handleSaveWorker = async () => {
    if (!editForm.name.trim()) { toast.error(t('workerNameRequired')); return; }
    try {
      await axios.put(`${API}/outsourcing/workers/${id}`, editForm, { headers: getAuthHeader() });
      toast.success(t('workerUpdated'));
      setIsEditOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update worker');
    }
  };

  // --- New job ---
  const handleOrderChange = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      setJobForm(f => ({ ...f, order_id: '', order_number: '', item_code: '', description: '', cost_per_piece: '' }));
      setSelectedOrderTotal(null);
      setSelectedOrderAssigned(0);
      return;
    }
    const orderTotal = (order.variants || []).reduce((s, v) => s + (v.quantity || 0), 0);
    const alreadyAssigned = jobs.filter(j => j.order_id === order.id).reduce((s, j) => s + (j.quantity || 0), 0);
    setSelectedOrderTotal(orderTotal);
    setSelectedOrderAssigned(alreadyAssigned);
    const rate = rates.find(r => r.item_code === order.item_code);
    setJobForm(f => ({
      ...f,
      order_id: order.id,
      order_number: order.order_number,
      item_code: order.item_code,
      description: order.items || '',
      cost_per_piece: rate ? rate.cost_per_piece : f.cost_per_piece,
    }));
  };

  const openJob = () => {
    setJobForm({ order_id: '', order_number: '', item_code: '', description: '', work_type: 'full', quantity: '', cost_per_piece: '', notes: '' });
    setSelectedOrderTotal(null);
    setSelectedOrderAssigned(0);
    setIsJobOpen(true);
  };

  const handleCreateJob = async () => {
    if (!jobForm.quantity || isNaN(jobForm.quantity) || parseInt(jobForm.quantity) <= 0) { toast.error(t('enterValidQuantity')); return; }
    if (selectedOrderTotal !== null) {
      const remaining = selectedOrderTotal - selectedOrderAssigned;
      if (parseInt(jobForm.quantity) > remaining) {
        toast.error(`Only ${remaining} pieces remaining for this order (total: ${selectedOrderTotal})`);
        return;
      }
    }
    if (!jobForm.cost_per_piece || isNaN(jobForm.cost_per_piece)) { toast.error(t('enterValidCost')); return; }
    const payload = {
      worker_id: id,
      worker_name: worker.name,
      order_id: jobForm.order_id || null,
      order_number: jobForm.order_number,
      item_code: jobForm.item_code,
      description: jobForm.description,
      work_type: jobForm.work_type,
      quantity: parseInt(jobForm.quantity),
      cost_per_piece: parseFloat(jobForm.cost_per_piece),
      notes: jobForm.notes,
    };
    try {
      await axios.post(`${API}/outsourcing/jobs`, payload, { headers: getAuthHeader() });
      toast.success(t('jobCreated'));
      setIsJobOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job');
    }
  };

  const handleComplete = async (job) => {
    try {
      await axios.put(`${API}/outsourcing/jobs/${job.id}/complete`, {}, { headers: getAuthHeader() });
      toast.success(t('jobCompleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete job');
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Delete job ${job.job_number}?`)) return;
    try {
      await axios.delete(`${API}/outsourcing/jobs/${job.id}`, { headers: getAuthHeader() });
      toast.success(t('jobDeleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    }
  };

  // --- Payments ---
  const openPay = () => {
    setPayForm({ amount: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' });
    setIsPayOpen(true);
  };

  const handleAddPayment = async () => {
    if (!payForm.amount || isNaN(payForm.amount) || parseFloat(payForm.amount) <= 0) { toast.error(t('enterValidAmount')); return; }
    if (!payForm.payment_date) { toast.error(t('paymentDateRequired')); return; }
    try {
      await axios.post(`${API}/outsourcing/payments`, {
        worker_id: id,
        worker_name: worker.name,
        amount: parseFloat(payForm.amount),
        payment_date: payForm.payment_date,
        notes: payForm.notes || null,
      }, { headers: getAuthHeader() });
      toast.success(t('paymentRecorded'));
      setIsPayOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDeletePayment = async (p) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${API}/outsourcing/payments/${p.id}`, { headers: getAuthHeader() });
      toast.success(t('paymentDeleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payment');
    }
  };

  if (loading) return (
    <div className="p-8 text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
      {t('loading')}
    </div>
  );
  if (!worker) return (
    <div className="p-8 text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
      {t('workerNotFound')}
    </div>
  );

  const liveCost = (parseFloat(jobForm.quantity) || 0) * (parseFloat(jobForm.cost_per_piece) || 0);

  return (
    <div className="p-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/outsourcing')}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            {language === 'ar' ? <ArrowRight className="w-3.5 h-3.5" /> : <ArrowLeft className="w-3.5 h-3.5" />}
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('backToOutsourcing')}</span>
          </button>
          <h1 className="text-3xl font-bold" lang={language === 'ar' ? 'ar' : 'en'}>{worker.name}</h1>
          {worker.phone && <p className="text-muted-foreground mt-0.5">{worker.phone}</p>}
          {worker.notes && (
            <p className="text-sm text-muted-foreground mt-1 italic" lang={language === 'ar' ? 'ar' : 'en'}>
              {worker.notes}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={openEdit}>
          <Pencil className="w-3.5 h-3.5 me-1" />
          <span lang={language === 'ar' ? 'ar' : 'en'}>{t('edit')}</span>
        </Button>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard label={t('totalWorkValue')} value={`${totalWork.toFixed(2)} ${t('currency')}`} language={language} />
        <StatCard label={t('totalPaid')} value={`${totalPaid.toFixed(2)} ${t('currency')}`} color="text-green-700" language={language} />
        <StatCard label={t('balanceRemaining')} value={`${balance.toFixed(2)} ${t('currency')}`} color={balance > 0 ? 'text-orange-600' : 'text-muted-foreground'} language={language} />
      </div>

      {/* Jobs section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('jobs')}</h2>
          <Button size="sm" onClick={openJob}>
            <Plus className="w-4 h-4 me-1" />
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('newJob')}</span>
          </Button>
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('jobNumber')}</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('orderNumber')}</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('itemCode')}</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('type')}</th>
                <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('quantity')}</th>
                <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('costPerPc')}</th>
                <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')}</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('status')}</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('assignedDate')}</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('completedDate')}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-6 text-center text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                    {t('noJobsYet')}
                  </td>
                </tr>
              )}
              {jobs.map(j => (
                <tr key={j.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="p-3 font-mono text-xs">{j.job_number}</td>
                  <td className="p-3">{j.order_number || '—'}</td>
                  <td className="p-3 font-mono text-xs">{j.item_code || '—'}</td>
                  <td className="p-3" lang={language === 'ar' ? 'ar' : 'en'}>
                    {j.work_type === 'full' ? t('full') : t('partial')}
                  </td>
                  <td className="p-3 text-end">{j.quantity}</td>
                  <td className="p-3 text-end">{parseFloat(j.cost_per_piece || 0).toFixed(2)}</td>
                  <td className="p-3 text-end font-medium">{parseFloat(j.total_cost || 0).toFixed(2)}</td>
                  <td className="p-3"><StatusBadge status={j.status} t={t} /></td>
                  <td className="p-3 text-xs text-muted-foreground">{j.assigned_date || '—'}</td>
                  <td className="p-3 text-xs text-muted-foreground">{j.completed_date || '—'}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      {j.status === 'assigned' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-blue-600 hover:text-blue-700"
                          title={t('jobCompleted')}
                          onClick={() => handleComplete(j)}
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {j.status === 'assigned' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteJob(j)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Payments section */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('payments')}</h2>
          <Button size="sm" onClick={openPay}>
            <Plus className="w-4 h-4 me-1" />
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('addPayment')}</span>
          </Button>
        </div>
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
                <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')} ({t('currency')})</th>
                <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                    {t('noPaymentsYet')}
                  </td>
                </tr>
              )}
              {payments.map(p => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="p-3">{p.payment_date}</td>
                  <td className="p-3 text-end font-medium text-green-700">{parseFloat(p.amount).toFixed(2)}</td>
                  <td className="p-3 text-muted-foreground text-xs" lang={language === 'ar' ? 'ar' : 'en'}>{p.notes || '—'}</td>
                  <td className="p-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDeletePayment(p)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            {payments.length > 0 && (
              <tfoot className="border-t bg-muted/20">
                <tr>
                  <td className="p-3 text-sm font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('totalPaidOf')}</td>
                  <td className="p-3 text-end font-bold text-green-700">{totalPaid.toFixed(2)}</td>
                  <td className="p-3 text-xs text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                    {t('of')} {totalWork.toFixed(2)} {t('currency')} {t('totalWorkValue').toLowerCase()}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </Card>
      </div>

      {/* Edit worker dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('editWorker')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('name')} *</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('phone')}</Label>
              <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
              <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsEditOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSaveWorker} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('saveChanges')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New job dialog */}
      <Dialog open={isJobOpen} onOpenChange={setIsJobOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('newJobFor')} {worker.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('orders')}</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={jobForm.order_id}
                onChange={e => handleOrderChange(e.target.value)}
              >
                <option value="">— {t('selectOrder')} —</option>
                {orders.map(o => (
                  <option key={o.id} value={o.id}>{o.order_number} — {o.item_code} ({o.client_name})</option>
                ))}
              </select>
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('itemCode')}</Label>
              <Input
                value={jobForm.item_code}
                onChange={e => setJobForm(f => ({ ...f, item_code: e.target.value }))}
                placeholder={t('autoFilledFromOrder')}
              />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('workType')}</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={jobForm.work_type}
                onChange={e => setJobForm(f => ({ ...f, work_type: e.target.value }))}
              >
                <option value="full">{t('full')}</option>
                <option value="partial">{t('partial')}</option>
              </select>
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('quantity')} *</Label>
              <Input
                type="number"
                min="1"
                max={selectedOrderTotal !== null ? selectedOrderTotal - selectedOrderAssigned : undefined}
                value={jobForm.quantity}
                onChange={e => setJobForm(f => ({ ...f, quantity: e.target.value }))}
                placeholder="0"
              />
              {selectedOrderTotal !== null && (
                <p className="text-xs text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>
                  {selectedOrderAssigned} / {selectedOrderTotal} {t('piecesAlreadyAssigned')} —{' '}
                  <span className="font-medium">{selectedOrderTotal - selectedOrderAssigned} {t('remaining')}</span>
                </p>
              )}
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</Label>
              <Input
                value={jobForm.description}
                onChange={e => setJobForm(f => ({ ...f, description: e.target.value }))}
                placeholder={t('autoFilledFromOrder')}
              />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('costPerPiece')} ({t('currency')}) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={jobForm.cost_per_piece}
                onChange={e => setJobForm(f => ({ ...f, cost_per_piece: e.target.value }))}
                placeholder={t('autoFilledFromRates')}
              />
            </div>
            {liveCost > 0 && (
              <p className="text-sm font-medium text-end" lang={language === 'ar' ? 'ar' : 'en'}>
                {t('totalCostLabel')} <span className="text-primary">{liveCost.toFixed(2)} {t('currency')}</span>
              </p>
            )}
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
              <Input value={jobForm.notes} onChange={e => setJobForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsJobOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleCreateJob} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('createJob')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add payment dialog */}
      <Dialog open={isPayOpen} onOpenChange={setIsPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addPayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')} *</Label>
              <Input
                type="date"
                value={payForm.payment_date}
                onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
              />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')} ({t('currency')}) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payForm.amount}
                onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>
                {t('balanceRemainingColon')} <span className="font-medium">{balance.toFixed(2)} {t('currency')}</span>
              </p>
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
              <Input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsPayOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleAddPayment} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('recordPayment')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
